import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faCalculator,
  faCalendar,
  faChartBar,
  faDownload,
  faEuroSign,
  faEye,
  faMinus,
  faMoneyBillTrendUp,
  faPrint,
  faShare,
} from '@fortawesome/free-solid-svg-icons';

const FinancialReportsPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedReport, setSelectedReport] = useState('overview');

  const breadcrumb = [{ label: 'Reporty' }, { label: 'Finanční reporty' }];

  const reportTypes = [
    { id: 'overview', name: 'Finanční přehled', icon: faChartBar, description: 'Celkový přehled příjmů a výdajů' },
    { id: 'revenue', name: 'Analýza příjmů', icon: faMoneyBillTrendUp, description: 'Detailní analýza tržeb' },
    { id: 'expenses', name: 'Analýza výdajů', icon: faCalculator, description: 'Rozdělení a kategorizace výdajů' },
    { id: 'profitability', name: 'Ziskovost', icon: faEuroSign, description: 'Analýza ziskovosti služeb' },
  ];

  const financialOverview = {
    totalRevenue: 156230,
    totalExpenses: 89120,
    netProfit: 67110,
    profitMargin: 42.9,
    revenueGrowth: 12.5,
    expenseGrowth: 8.3,
    workingDays: 22,
    dailyAverage: 7101,
  };

  const monthlyComparison = [
    { month: 'Září 2024', revenue: 138450, expenses: 82300, profit: 56150 },
    { month: 'Říjen 2024', revenue: 142680, expenses: 85420, profit: 57260 },
    { month: 'Listopad 2024', revenue: 156230, expenses: 89120, profit: 67110 },
  ];

  const revenueByService = [
    { service: 'Střih a foukaná', revenue: 78400, percentage: 50.2, cost: 23520, profit: 54880 },
    { service: 'Barvení vlasů', revenue: 45000, percentage: 28.8, cost: 18000, profit: 27000 },
    { service: 'Kosmetické ošetření', revenue: 18000, percentage: 11.5, cost: 5400, profit: 12600 },
    { service: 'Manikúra', revenue: 11400, percentage: 7.3, cost: 3420, profit: 7980 },
    { service: 'Holení', revenue: 3430, percentage: 2.2, cost: 686, profit: 2744 },
  ];

  const expenseCategories = [
    { category: 'Materiál', amount: 32150, percentage: 36.1, trend: 'up', trendValue: 5.2 },
    { category: 'Mzdy', amount: 28400, percentage: 31.9, trend: 'up', trendValue: 3.8 },
    { category: 'Pronájem', amount: 15600, percentage: 17.5, trend: 'stable', trendValue: 0 },
    { category: 'Energie', amount: 8900, percentage: 10.0, trend: 'up', trendValue: 12.5 },
    { category: 'Marketing', amount: 2800, percentage: 3.1, trend: 'down', trendValue: -8.3 },
    { category: 'Ostatní', amount: 1270, percentage: 1.4, trend: 'down', trendValue: -15.2 },
  ];

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return faArrowUp;
      case 'down':
        return faArrowDown;
      default:
        return faMinus;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const renderOverviewReport = () => (
    <div className="space-y-6">
      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-50 rounded-lg">
              <FontAwesomeIcon icon={faEuroSign} className="text-xl text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Celkové příjmy</p>
              <p className="text-2xl font-bold text-gray-900">{financialOverview.totalRevenue.toLocaleString()} Kč</p>
              <div className="flex items-center text-sm mt-1">
                <FontAwesomeIcon icon={faArrowUp} className="w-3 h-3 mr-1 text-green-600" />
                <span className="text-green-600">+{financialOverview.revenueGrowth}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-red-50 rounded-lg">
              <FontAwesomeIcon icon={faCalculator} className="text-xl text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Celkové výdaje</p>
              <p className="text-2xl font-bold text-gray-900">{financialOverview.totalExpenses.toLocaleString()} Kč</p>
              <div className="flex items-center text-sm mt-1">
                <FontAwesomeIcon icon={faArrowUp} className="w-3 h-3 mr-1 text-red-600" />
                <span className="text-red-600">+{financialOverview.expenseGrowth}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-50 rounded-lg">
              <FontAwesomeIcon icon={faChartBar} className="text-xl text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Čistý zisk</p>
              <p className="text-2xl font-bold text-gray-900">{financialOverview.netProfit.toLocaleString()} Kč</p>
              <div className="flex items-center text-sm mt-1">
                <span className="text-blue-600">{financialOverview.profitMargin}% marže</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-50 rounded-lg">
              <FontAwesomeIcon icon={faCalendar} className="text-xl text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Denní průměr</p>
              <p className="text-2xl font-bold text-gray-900">{financialOverview.dailyAverage.toLocaleString()} Kč</p>
              <div className="flex items-center text-sm mt-1">
                <span className="text-purple-600">{financialOverview.workingDays} pracovních dnů</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Comparison */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Měsíční srovnání</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Měsíc</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Příjmy</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Výdaje</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zisk</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marže</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {monthlyComparison.map((month, index) => {
                const margin = Math.round((month.profit / month.revenue) * 100);
                return (
                  <tr key={index} className={index === monthlyComparison.length - 1 ? 'bg-green-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{month.month}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month.revenue.toLocaleString()} Kč</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month.expenses.toLocaleString()} Kč</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{month.profit.toLocaleString()} Kč</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{margin}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderRevenueReport = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Příjmy podle služeb</h3>
        <div className="space-y-4">
          {revenueByService.map((service, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{service.service}</span>
                <div className="text-right">
                  <span className="text-sm font-bold text-gray-900">{service.revenue.toLocaleString()} Kč</span>
                  <span className="text-xs text-gray-500 ml-2">({service.percentage}%)</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${service.percentage}%` }}></div>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Náklady: {service.cost.toLocaleString()} Kč</span>
                <span>
                  Zisk: {service.profit.toLocaleString()} Kč ({Math.round((service.profit / service.revenue) * 100)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderExpensesReport = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Výdaje podle kategorií</h3>
        <div className="space-y-4">
          {expenseCategories.map((category, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{category.category}</span>
                <div className="flex items-center">
                  <span className="text-sm font-bold text-gray-900 mr-2">{category.amount.toLocaleString()} Kč</span>
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={getTrendIcon(category.trend)} className={`w-3 h-3 mr-1 ${getTrendColor(category.trend)}`} />
                    <span className={`text-xs ${getTrendColor(category.trend)}`}>
                      {category.trendValue > 0 ? '+' : ''}
                      {category.trendValue}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-red-600 h-2 rounded-full transition-all duration-300" style={{ width: `${category.percentage}%` }}></div>
              </div>
              <div className="text-xs text-gray-500">{category.percentage}% z celkových výdajů</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderProfitabilityReport = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Ziskovost služeb</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Služba</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Příjmy</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Náklady</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zisk</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marže</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ROI</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {revenueByService.map((service, index) => {
                const margin = Math.round((service.profit / service.revenue) * 100);
                const roi = Math.round((service.profit / service.cost) * 100);
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{service.service}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{service.revenue.toLocaleString()} Kč</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{service.cost.toLocaleString()} Kč</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{service.profit.toLocaleString()} Kč</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          margin > 50 ? 'bg-green-100 text-green-800' : margin > 30 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {margin}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          roi > 200 ? 'bg-green-100 text-green-800' : roi > 100 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {roi}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSelectedReport = () => {
    switch (selectedReport) {
      case 'overview':
        return renderOverviewReport();
      case 'revenue':
        return renderRevenueReport();
      case 'expenses':
        return renderExpensesReport();
      case 'profitability':
        return renderProfitabilityReport();
      default:
        return renderOverviewReport();
    }
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Finanční reporty" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Finanční reporty</h1>
                <p className="text-gray-600 mt-1">Detailní finanční analýza a reporty</p>
              </div>
              <div className="flex items-center space-x-3">
                <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="week">Tento týden</option>
                  <option value="month">Tento měsíc</option>
                  <option value="quarter">Toto čtvrtletí</option>
                  <option value="year">Tento rok</option>
                </select>
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Report Type Selection */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Typ reportu</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {reportTypes.map(report => (
                <button
                  key={report.id}
                  onClick={() => setSelectedReport(report.id)}
                  className={`p-4 rounded-lg border-2 transition-colors text-left ${
                    selectedReport === report.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <FontAwesomeIcon icon={report.icon} className="w-6 h-6 text-gray-600" />
                    <h4 className="font-medium text-gray-900">{report.name}</h4>
                  </div>
                  <p className="text-sm text-gray-600">{report.description}</p>
                </button>
              ))}
            </div>
          </section>

          {/* Report Content */}
          {renderSelectedReport()}

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export do Excel</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faPrint} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Tisknout report</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faShare} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Sdílet s účetním</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faEye} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Vytvořit dashboard</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FinancialReportsPage;
